import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import Autocomplete from "../components/Autocomplete";
import QuickActions from "../components/QuickActions";
import CompanyNotesTasks from "../components/CompanyNotesTasks";
import ClientFiles from "../components/ClientFiles";
import SentComms from "../components/SentComms";
import { Donut, dueStats } from "./Contabilita";

const EUR = (n) => "€ " + Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const daysAgo = (v) => {
  if (!v) return null;
  const d = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  return d <= 0 ? "oggi" : d === 1 ? "ieri" : `${d} giorni fa`;
};

function Kpi({ label, value, sub, to, tint, testid }) {
  const inner = (
    <>
      <div className="ws-kpi-value" style={tint ? { color: tint } : undefined}>{value}</div>
      <div className="ws-kpi-label">{label}</div>
      {sub && <div className="ws-kpi-sub">{sub}</div>}
    </>
  );
  return to
    ? <Link className="ws-kpi" to={to} data-testid={testid}>{inner}</Link>
    : <div className="ws-kpi" data-testid={testid}>{inner}</div>;
}

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

function regDetail(client, year, monthlyTasks, annualTasks) {
  const now = new Date();
  const curY = now.getFullYear();
  const prevMonths = now.getMonth();
  const isForf = client.regime === "forfettario";
  let monthsDue;
  if (year < curY) monthsDue = 12;
  else if (year > curY) monthsDue = 0;
  else monthsDue = isForf ? 0 : prevMonths;
  const rows = [];
  for (const t of monthlyTasks) {
    let done = 0, upto = 0;
    for (let m = 1; m <= monthsDue; m++) {
      if (client.checks[`m${m}:${t.key}`]) { done += 1; if (upto === m - 1) upto = m; }
    }
    rows.push({ key: t.key, label: t.label, due: monthsDue, done, upto, annual: false });
  }
  const WHEN = { marzo: 3, giugno: 6, luglio: 7, dicembre: 12 };
  for (const t of annualTasks) {
    const wm = WHEN[t.when] || 12;
    if (year < curY || (year === curY && wm <= prevMonths)) {
      rows.push({ key: t.key, label: `${t.label} (${t.when})`, due: 1, done: client.checks[`a:${t.key}`] ? 1 : 0, upto: 0, annual: true });
    }
  }
  return rows;
}

function RegRow({ r }) {
  const full = r.due > 0 && r.done >= r.due;
  const col = full ? "#39ff14" : r.done / Math.max(r.due, 1) >= 0.5 ? "#facc15" : "#fb7185";
  let stato;
  if (r.annual) stato = full ? "fatto ✅" : "da fare";
  else if (full) stato = `in regola (aggiornate a ${MESI[r.due - 1]}) ✅`;
  else if (r.upto > 0) stato = `aggiornate a ${MESI[r.upto - 1]}${r.done > r.upto ? " (con buchi)" : ""} — mancano ${r.due - r.done}`;
  else stato = r.done > 0 ? `${r.done}/${r.due} (con buchi)` : "da iniziare";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, padding: "2px 0" }} data-testid={`ws-reg-${r.key}`}>
      <span style={{ width: 9, height: 9, borderRadius: "50%", background: col, flex: "0 0 auto" }} />
      <span style={{ minWidth: 120 }}><strong>{r.label}</strong></span>
      <span style={{ color: col }}>{stato}</span>
      {!r.annual && <span className="muted" style={{ fontSize: 12 }}>({r.done}/{r.due})</span>}
    </div>
  );
}

const REGIMI = ["forfettario", "semplificato", "ordinario"];
const IVA_OPTS = ["mensile", "trimestrale", "esente"];

function ClientOverview({ company, reg, regColor, regRows, regYear, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState(null);
  const [callNote, setCallNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteOk, setNoteOk] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const loadCallHistory = (cid) => {
    api.get(`/companies/${cid}/notes`, { noCache: true })
      .then((r) => setCallHistory(r.data.filter((n) => (n.text || "").startsWith("📞 Telefonata:")).slice(0, 3)))
      .catch(() => setCallHistory([]));
  };
  useEffect(() => {
    setDocs(null); setCallNote(""); setNoteOk(false);
    api.get(`/companies/${company.id}/recent-docs`, { noCache: true }).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    loadCallHistory(company.id);
  }, [company.id]);
  const saveCallNote = async () => {
    if (!callNote.trim()) return;
    setNoteSaving(true);
    try {
      await api.post(`/companies/${company.id}/notes`, { text: `📞 Telefonata: ${callNote.trim()}` });
      setCallNote(""); setNoteOk(true);
      loadCallHistory(company.id);
      setTimeout(() => setNoteOk(false), 4000);
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setNoteSaving(false);
  };
  const startEdit = () => {
    setF({ activity: company.activity || "", regime: company.regime || "", iva_periodicity: company.iva_periodicity || "", billing_period: company.billing_period || "" });
    setEdit(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}`, {
        activity: f.activity.trim(), regime: f.regime,
        iva_periodicity: f.iva_periodicity, billing_period: f.billing_period,
      });
      setEdit(false); onSaved();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
    setSaving(false);
  };
  const Item = ({ ico, lbl, children }) => (
    <div style={{ minWidth: 150 }}>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>{ico} {lbl}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{children}</div>
    </div>
  );
  const sede = [company.address, [company.zip_code, company.city].filter(Boolean).join(" "), company.province ? `(${company.province})` : ""].filter(Boolean).join(", ");
  return (
    <div className="panel" data-testid="ws-overview" style={{ borderColor: "rgba(34,211,238,.35)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <h3 style={{ margin: 0 }}>📋 Quadro cliente</h3>
        <div className="spacer" style={{ flex: 1 }} />
        {!edit
          ? <button className="btn sm" onClick={startEdit} data-testid="ws-overview-edit">✏️ Modifica</button>
          : <>
              <button className="btn sm" onClick={() => setEdit(false)}>Annulla</button>
              <button className="btn sm primary" onClick={save} disabled={saving} data-testid="ws-overview-save">{saving ? "Salvo…" : "Salva"}</button>
            </>}
      </div>
      {!edit ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 28px", marginTop: 10 }}>
          <Item ico="🏭" lbl="Cosa fa">{company.activity || <span className="muted">— da compilare —</span>}</Item>
          <Item ico="📊" lbl="Regime contabile">{company.regime ? <b style={{ textTransform: "capitalize" }}>{company.regime}</b> : <span className="muted">—</span>}</Item>
          <Item ico="🧾" lbl="Periodicità IVA">{company.iva_periodicity ? <b style={{ textTransform: "capitalize" }}>{company.iva_periodicity}</b> : <span className="muted">—</span>}</Item>
          <Item ico="📒" lbl={`Registrazioni ${regYear || ""}`}>
            {reg && reg.due > 0
              ? <b style={{ color: regColor }}>{reg.done}/{reg.due} ({Math.round(reg.ratio * 100)}%)</b>
              : <span className="muted">{company.regime ? "nessun adempimento nel periodo" : "assegna un regime"}</span>}
          </Item>
          <Item ico="💶" lbl="Compensi">{company.billing_period ? <span style={{ textTransform: "capitalize" }}>{company.billing_period}</span> : <span className="muted">auto</span>}</Item>
          <Item ico="🆔" lbl="P.IVA / CF">{company.vat_number || company.fiscal_code || <span className="muted">—</span>}</Item>
          {company.pec && <Item ico="📮" lbl="PEC">{company.pec}</Item>}
          {company.sdi_code && <Item ico="🔁" lbl="SDI">{company.sdi_code}</Item>}
          {sede && <Item ico="📍" lbl="Sede">{sede}</Item>}
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginTop: 10 }}>
          <div className="field" style={{ flex: "1 1 320px" }}><label>🏭 Cosa fa (attività)</label>
            <input value={f.activity} onChange={(e) => setF({ ...f, activity: e.target.value })} placeholder="es. Bar e piccola ristorazione" data-testid="ws-edit-activity" />
          </div>
          <div className="field"><label>📊 Regime</label>
            <select value={f.regime} onChange={(e) => setF({ ...f, regime: e.target.value })} data-testid="ws-edit-regime">
              <option value="">—</option>{REGIMI.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="field"><label>🧾 Periodicità IVA</label>
            <select value={f.iva_periodicity} onChange={(e) => setF({ ...f, iva_periodicity: e.target.value })} data-testid="ws-edit-iva">
              <option value="">—</option>{IVA_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="field"><label>💶 Compensi</label>
            <select value={f.billing_period} onChange={(e) => setF({ ...f, billing_period: e.target.value })} data-testid="ws-edit-billing">
              <option value="">auto</option>
              {["mensile", "bimestrale", "trimestrale", "semestrale", "annuale", "cessato"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
      )}
      {!edit && regRows && regRows.length > 0 && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 8 }} data-testid="ws-reg-detail">
          <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>📒 Dettaglio registrazioni {regYear}</div>
          {regRows.map((r) => <RegRow key={r.key} r={r} />)}
        </div>
      )}
      {!edit && (
        <div style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 8, display: "flex", flexWrap: "wrap", gap: "12px 32px" }}>
          <div style={{ flex: "1 1 340px", minWidth: 280 }} data-testid="ws-recent-docs">
            <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>📎 Ultimi documenti ricevuti</div>
            {docs === null ? <span className="muted" style={{ fontSize: 13 }}>Carico…</span>
              : docs.length === 0 ? <span className="muted" style={{ fontSize: 13 }}>Nessun documento registrato.</span>
              : docs.map((d, i) => (
                <div key={i} style={{ fontSize: 13, padding: "2px 0", display: "flex", gap: 6, alignItems: "baseline" }}>
                  <span className="muted" style={{ fontSize: 12, minWidth: 64 }}>{d.date ? new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}</span>
                  {d.kind === "f24"
                    ? <span>🧾 <strong>{d.name}</strong>{d.amount ? ` · € ${d.amount}` : ""}{d.due_date ? ` · scad. ${d.due_date.split("-").reverse().join("/")}` : ""} {d.paid ? "✅ pagato" : "🕓"}</span>
                    : <span>📄 {d.name} <span className="muted" style={{ fontSize: 12 }}>({d.source})</span></span>}
                </div>
              ))}
          </div>
          <div style={{ flex: "1 1 340px", minWidth: 280 }} data-testid="ws-call-note">
            <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>📞 Nota telefonata</div>
            <textarea rows={3} value={callNote} onChange={(e) => setCallNote(e.target.value)}
              placeholder="Appunta qui cosa vi siete detti… (finisce negli appunti del cliente)"
              style={{ width: "100%" }} data-testid="ws-call-note-input" />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
              <button className="btn sm primary" onClick={saveCallNote} disabled={noteSaving || !callNote.trim()} data-testid="ws-call-note-save">
                {noteSaving ? "Salvo…" : "💾 Salva negli appunti"}
              </button>
              {noteOk && <span style={{ color: "#39ff14", fontSize: 13 }} data-testid="ws-call-note-ok">✅ Salvata nello storico</span>}
            </div>
            {callHistory.length > 0 && (
              <div style={{ marginTop: 8 }} data-testid="ws-call-history">
                <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 2 }}>Ultime telefonate</div>
                {callHistory.map((n) => (
                  <div key={n.id} style={{ fontSize: 12.5, padding: "2px 0", display: "flex", gap: 6, alignItems: "baseline" }}>
                    <span className="muted" style={{ fontSize: 11.5, minWidth: 64, flex: "0 0 auto" }}>
                      {n.created_at ? new Date(n.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—"}
                    </span>
                    <span>{(n.text || "").replace("📞 Telefonata:", "").trim()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Workspace() {
  const [clients, setClients] = useState([]);
  const [selId, setSelId] = useState(() => localStorage.getItem("workspace_client") || "");
  const [detail, setDetail] = useState(null);   // {company, people, projects}
  const [kpi, setKpi] = useState(null);
  const [board, setBoard] = useState(null);
  const [mails, setMails] = useState(null);
  const [taskByMail, setTaskByMail] = useState({});
  const [mailLimit, setMailLimit] = useState(30);
  const [mailFilter, setMailFilter] = useState("tutte");

  useEffect(() => {
    api.get("/companies").then((r) => setClients(r.data.filter((c) => c.kind === "cliente"))).catch(() => {});
    api.get(`/contabilita/board?year=${new Date().getFullYear()}`).then((r) => setBoard(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selId) return;
    localStorage.setItem("workspace_client", selId);
    setDetail(null); setKpi(null); setMails(null); setMailLimit(30); setMailFilter("tutte");
    api.get(`/companies/${selId}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
    api.get(`/companies/${selId}/workspace`).then((r) => setKpi(r.data)).catch(() => {});
    api.get(`/emails?status=tutte&company=${selId}`).then((r) => {
      const list = [...r.data].sort((a, b) => String(b.received_at || "").localeCompare(String(a.received_at || "")));
      setMails(list);
    }).catch(() => setMails([]));
    api.get("/tasks?status=da_fare").then((r) => {
      const map = {};
      r.data.forEach((t) => { if (t.email_id) map[t.email_id] = (map[t.email_id] || 0) + 1; });
      setTaskByMail(map);
    }).catch(() => {});
  }, [selId]);

  const company = detail?.company;
  const regClient = board?.clients?.find((c) => c.id === selId);
  let reg = null;
  let regRows = null;
  if (regClient && board) {
    const ex = regClient.excluded || [];
    const mT = (board.tasks[regClient.regime]?.monthly || []).filter((t) => !ex.includes(t.key));
    const aT = (board.tasks[regClient.regime]?.annual || []).filter((t) => !ex.includes(t.key));
    reg = dueStats(regClient, board.year, mT, aT);
    regRows = regDetail(regClient, board.year, mT, aT);
  }
  const regColor = !reg || reg.due === 0 ? "#94a3b8" : reg.ratio >= 0.999 ? "#39ff14" : reg.ratio >= 0.7 ? "#a3e635" : reg.ratio >= 0.4 ? "#facc15" : "#fb7185";

  return (
    <div>
      <h1 className="page-title">Area di lavoro</h1>
      <p className="muted" style={{ marginTop: -6 }}>Scegli un cliente e gestisci tutto da qui: email, appuntamenti, appunti, registrazioni e numeri.</p>

      <div className="panel" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ minWidth: 280, flex: "0 1 380px" }}>
          <Autocomplete
            items={clients.map((c) => ({ id: c.id, label: c.name }))}
            placeholder="Cerca cliente…"
            initialText={clients.find((c) => c.id === selId)?.name || ""}
            onSelect={(it) => setSelId(it.id)}
            testid="ws-client-picker"
          />
        </div>
        {company && (
          <>
            <QuickActions target={{ kind: "company", id: company.id, name: company.name, email: company.email, phone: company.phone, company: company.name }} />
            <div className="spacer" />
            <Link className="btn sm" to={`/aziende/${company.id}`} data-testid="ws-open-full">Apri scheda completa →</Link>
          </>
        )}
      </div>

      {!selId && <div className="panel muted" data-testid="ws-empty">Seleziona un cliente per iniziare.</div>}

      {company && (
        <>
          <ClientOverview company={company} reg={reg} regColor={regColor} regRows={regRows} regYear={board?.year}
            onSaved={() => api.get(`/companies/${selId}`).then((r) => setDetail(r.data)).catch(() => {})} />
          <div className="ws-kpis" data-testid="ws-kpis">
            <Kpi label="Ultimo contatto" testid="ws-kpi-last-contact"
              value={kpi ? (daysAgo(kpi.last_contact?.date) || "—") : "…"}
              sub={kpi?.last_contact ? `${{ appuntamento: "📅", whatsapp: "💬", sms: "📱" }[kpi.last_contact.kind] || "✉️"}${kpi.last_contact.direction === "out" ? " ↗" : ""} ${fmtDate(kpi.last_contact.date)}${kpi.last_contact.subject ? " · " + kpi.last_contact.subject.slice(0, 40) : ""}` : "nessun contatto registrato"} />
            <Kpi label={`Fatturato ${kpi?.year || new Date().getFullYear()}`} testid="ws-kpi-revenue"
              value={kpi ? EUR(kpi.revenue_ytd) : "…"} sub={kpi ? `${kpi.invoices_ytd} fatture emesse` : ""} />
            <Kpi label="Proiezione fine anno" testid="ws-kpi-projection"
              value={kpi ? EUR(kpi.revenue_projection) : "…"} sub="al ritmo attuale" />
            <Kpi label="Registrazioni" to="/contabilita" tint={regColor} testid="ws-kpi-reg"
              value={reg ? (reg.due > 0 ? `${Math.round(reg.ratio * 100)}%` : "—") : company.regime ? "…" : "no regime"}
              sub={reg && reg.due > 0 ? `${reg.done}/${reg.due} adempimenti · ${regClient?.regime || ""}` : regClient?.regime || "assegna un regime"} />
            <Kpi label="Tempo dedicato" testid="ws-kpi-hours"
              value={`${Number(company.estimated_hours || 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })} h`}
              sub={`${company.docs_used || company.documents_count || 0} documenti · ${company.emails_used || 0} email`} />
            <Kpi label="Margine annuo" tint={Number(company.annual_margin) < 0 ? "#fb7185" : "#39ff14"} testid="ws-kpi-margin"
              value={EUR(company.annual_margin)} sub={company.reference_year ? `anno ${company.reference_year}` : ""} />
          </div>

          <div className="ws-cols">
            <div>
              <div className="panel" data-testid="ws-mails">
                <div className="toolbar" style={{ marginTop: 0 }}>
                  <h3 style={{ margin: 0 }}>Email del cliente {mails && <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>({mails.length})</span>}</h3>
                  <div className="spacer" />
                  {["tutte", "da_gestire", "gestita"].map((f) => (
                    <button key={f} className={"btn sm" + (mailFilter === f ? " primary" : "")} onClick={() => setMailFilter(f)} data-testid={`ws-mail-filter-${f}`}>
                      {f === "tutte" ? "Tutte" : f === "da_gestire" ? "Da gestire" : "Gestite"}
                    </button>
                  ))}
                </div>
                {!mails && <p className="muted">Caricamento…</p>}
                {mails && mails.length === 0 && <p className="muted">Nessuna email trovata per questo cliente.</p>}
                {mails && mails.filter((m) => mailFilter === "tutte" || m.status === mailFilter).slice(0, mailLimit).map((m) => (
                  <Link key={m.id} to={`/email/${m.id}`} state={{ backTo: "/area-di-lavoro", backLabel: "Area di lavoro" }} className="ws-mail-row" data-testid={`ws-mail-${m.id}`}>
                    <span className="ws-mail-date">{fmtDate(m.received_at)}</span>
                    <span className="ws-mail-subj">
                      <b>{m.subject || "(senza oggetto)"}</b>
                      <span className="muted" style={{ display: "block", fontSize: 11 }}>{m.from_name || m.from_email}</span>
                    </span>
                    {taskByMail[m.id] > 0 && <span className="ws-mail-task" title="Incarichi aperti nati da questa email">📌 {taskByMail[m.id]}</span>}
                    <span className={"pill " + (m.status === "gestita" ? "gestita" : "da_gestire")}>{m.status === "gestita" ? "gestita" : "da gestire"}</span>
                  </Link>
                ))}
                {mails && mails.filter((m) => mailFilter === "tutte" || m.status === mailFilter).length > mailLimit && (
                  <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setMailLimit((n) => n + 50)} data-testid="ws-mail-more">Mostra altre</button>
                )}
              </div>
              <ClientFiles companyId={selId} />
              <SentComms params={`company=${company.id}`} testid="ws-sent" />
            </div>
            <div>
              <div className="panel" data-testid="ws-people">
                <h3>Persone collegate</h3>
                {(detail.people || []).length === 0 && <p className="muted">Nessuna persona collegata.</p>}
                {(detail.people || []).map((p) => (
                  <div key={p.link_id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                    <Link to={`/persone/${p.person.id}`}>{p.person.first_name} {p.person.last_name}</Link>
                    {p.role && <span className="muted" style={{ fontSize: 12 }}> · {p.role}</span>}
                    {p.person.email && <div className="muted" style={{ fontSize: 11 }}>{p.person.email}</div>}
                  </div>
                ))}
              </div>
              <div className="panel" data-testid="ws-folders">
                <h3>Cartelle e documenti</h3>
                {(detail.projects || []).length === 0 && <p className="muted">Nessuna cartella.</p>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(detail.projects || []).map((pr) => (
                    <Link key={pr.id} className="btn sm" to={`/progetti/${pr.id}`} data-testid={`ws-project-${pr.id}`}>📁 {pr.name}</Link>
                  ))}
                </div>
              </div>
              <CompanyNotesTasks companyId={company.id} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
